class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.productContainer = document.getElementById('cart-notification-product');

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    // DO v533: On iPhone Safari, fixed elements inside the header can be clipped
    // or placed under the mobile browser UI. Move the notification itself to
    // the body on mobile so the added-cart contents display on all phones.
    if (window.matchMedia('(max-width: 749px)').matches && this.notification.parentElement !== document.body) {
      document.body.appendChild(this.notification);
    }

    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    // If iOS skips the transition event, still focus/trap after the paint.
    window.setTimeout(() => {
      if (this.notification.classList.contains('active')) {
        this.notification.focus();
        trapFocus(this.notification);
      }
    }, 120);

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const container = document.getElementById(section.id);
      if (!container || !parsedState.sections || !parsedState.sections[section.id]) return;
      container.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.renderAddedItems(parsedState);
    this.renderCartItemsFallback(parsedState);

    if (this.header) this.header.reveal();
    this.open();
  }

  renderAddedItems(parsedState) {
    if (!this.productContainer) return;

    const items = Array.isArray(parsedState?.items)
      ? parsedState.items
      : parsedState && (parsedState.id || parsedState.title || parsedState.product_title)
        ? [parsedState]
        : [];

    if (!items.length) {
      if (parsedState?.sections?.['cart-notification-product']) {
        this.productContainer.innerHTML = this.getSectionInnerHTML(
          parsedState.sections['cart-notification-product'],
          '.shopify-section'
        );
      }
      return;
    }

    const escapeHtml = (value) =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const getItemImage = (item) => {
      const image = item?.image || item?.featured_image?.url || item?.featured_image || item?.image_url || '';
      if (!image) return '';
      if (typeof image === 'string') return image;
      return image?.url || image?.src || '';
    };

    const rows = items
      .map((item) => {
        const title = escapeHtml(item?.product_title || item?.title || 'Added item');
        const image = getItemImage(item);
        const imageMarkup = image
          ? `<div class="cart-notification-product__image global-media-settings"><img src="${escapeHtml(image)}" alt="${title}" width="70" height="70" loading="lazy"></div>`
          : '';
        return `<div class="cart-notification-product-item">${imageMarkup}<div class="cart-notification-product__details"><h3 class="cart-notification-product__name h4">${title}</h3></div></div>`;
      })
      .join('');

    this.productContainer.innerHTML = `<div class="cart-notification-product-list">${rows}</div>`;
  }

  renderCartItemsFallback(parsedState) {
    if (!this.productContainer) return;

    const hasRenderedItems = this.productContainer.querySelector('.cart-notification-product-item');
    if (hasRenderedItems) return;

    const cartUrl = (window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js';
    fetch(cartUrl, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (!cart || !Array.isArray(cart.items) || !cart.items.length) return;

        const escapeHtml = (value) =>
          String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');

        const rows = cart.items
          .map((item) => {
            const title = escapeHtml(item?.product_title || item?.title || 'Added item');
            const image = item?.image || item?.featured_image?.url || item?.featured_image || '';
            const imageMarkup = image
              ? `<div class="cart-notification-product__image global-media-settings"><img src="${escapeHtml(image)}" alt="${title}" width="70" height="70" loading="lazy"></div>`
              : '';
            return `<div class="cart-notification-product-item">${imageMarkup}<div class="cart-notification-product__details"><h3 class="cart-notification-product__name h4">${title}</h3></div></div>`;
          })
          .join('');

        this.productContainer.innerHTML = `<div class="cart-notification-product-list">${rows}</div>`;
      })
      .catch(() => {});
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('#cart-notification') && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
